import { supabase } from './supabase';
import { isPreviewAdminMode } from './preview';

export const MFA_TRUSTED_KEY = 'plotwise_mfa_trusted_until';

/**
 * Tjekker om denne browser-enhed er gemt som betroet for DENNE bruger i op til 30 dage.
 * Betroet status er bundet til bruger-id, så en anden konto på samme enhed ikke arver den.
 */
export function isDeviceMfaTrusted(userId?: string | null): boolean {
  if (!userId || typeof window === 'undefined' || typeof localStorage === 'undefined') return false;
  try {
    const raw = localStorage.getItem(MFA_TRUSTED_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { userId?: string; expiresAt?: number };
    return (
      parsed.userId === userId &&
      typeof parsed.expiresAt === 'number' &&
      Number.isFinite(parsed.expiresAt) &&
      parsed.expiresAt > Date.now()
    );
  } catch {
    return false;
  }
}

/**
 * Gemmer eller fjerner enhedens betroede status for en bruger (30 dages levetid).
 */
export function setDeviceMfaTrusted(trusted: boolean, userId?: string | null): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  try {
    if (trusted && userId) {
      const thirtyDays = Date.now() + 30 * 24 * 60 * 60 * 1000;
      localStorage.setItem(MFA_TRUSTED_KEY, JSON.stringify({ userId, expiresAt: thirtyDays }));
    } else {
      localStorage.removeItem(MFA_TRUSTED_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * Afgør om en bruger skal tvinges igennem 2FA.
 * Kun professionelle rådgivere og admins skal have 2FA.
 * Almindelige jægere (B2C), demo-konti og automatiserede test-browsere fritages.
 */
export function isMfaRequiredForAccount(
  user: { id?: string; email?: string | null } | null,
  accountType: string,
  isAdmin: boolean,
): boolean {
  if (!user || !user.email) return false;

  // 1. Demo-brugeren skal aldrig have 2FA
  if (user.id === 'demo' || user.email === 'eksempel@plotwise.dk') return false;

  // 2-3. Test-undtagelser (automatiserede browsere, testdomæner, seedede konti) findes KUN i
  // udviklings- og testbuilds. I produktion kan de ikke slås til af en angriber.
  if (import.meta.env.DEV) {
    if (typeof navigator !== 'undefined' && navigator.webdriver) return false;
    const lowerEmail = user.email.trim().toLowerCase();
    if (
      lowerEmail.endsWith('.test') ||
      lowerEmail.endsWith('.invalid') ||
      lowerEmail.startsWith('test@') ||
      lowerEmail.startsWith('fixture@') ||
      lowerEmail.includes('eksempel.dk') ||
      lowerEmail.includes('example.com') ||
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('account-seeded') === '1')
    ) {
      return false;
    }
  }

  // 4. Preview URL parametre (superadmin preview m.v.)
  if (isPreviewAdminMode()) return false;

  // 5. Kun hvis brugeren har professional tier eller er admin
  return accountType === 'professional' || isAdmin;
}

/**
 * Sender 6-cifret engangskode på e-mail via Supabase Auth OTP.
 * Koden genereres og verificeres udelukkende af serveren (Supabase), aldrig i browseren.
 */
export async function sendMfaEmailOtp(
  email: string,
): Promise<{ success: boolean; message?: string }> {
  const normalized = email.trim().toLowerCase();
  if (!supabase) {
    return {
      success: false,
      message: 'Engangskode kan ikke sendes uden forbindelse til serveren.',
    };
  }
  try {
    const { error } = await supabase.auth.signInWithOtp({
      email: normalized,
      options: { shouldCreateUser: false },
    });
    if (error) {
      return {
        success: false,
        message: 'Engangskoden kunne ikke sendes. Vent lidt og prøv igen.',
      };
    }
    return { success: true };
  } catch {
    return {
      success: false,
      message: 'Engangskoden kunne ikke sendes. Vent lidt og prøv igen.',
    };
  }
}

/**
 * Verificerer den 6-cifrede engangskode mod Supabase Auth. Der findes ingen nødkoder.
 */
export async function verifyMfaEmailOtp(email: string, token: string): Promise<boolean> {
  const normalized = email.trim().toLowerCase();
  const cleanToken = token.trim().replace(/\s+/g, '');

  if (!cleanToken || cleanToken.length < 6 || !supabase) return false;

  try {
    const { error } = await supabase.auth.verifyOtp({
      email: normalized,
      token: cleanToken,
      type: 'email',
    });
    return !error;
  } catch {
    return false;
  }
}

/**
 * Finder en engangskode (6-8 cifre) i en tekst, fx indsat fra en mail. Kan stå som "1234 5678",
 * "123-456" eller midt i en sætning ("Din kode er 12345678"). Returnerer kun cifrene, ellers ''.
 */
export function extractOtp(text: string): string {
  const match = text.match(/\b(\d{3,4})[\s-]?(\d{3,4})\b/);
  if (match) {
    const digits = match[1] + match[2];
    if (digits.length >= 6 && digits.length <= 8) return digits;
  }
  const plain = text.match(/\b\d{6,8}\b/);
  return plain ? plain[0] : '';
}

/** Viser koden i grupper på fire cifre, så den er nem at læse. */
export function formatOtp(digits: string): string {
  const clean = digits.replace(/\D/g, '').slice(0, 8);
  return clean.length > 4 ? `${clean.slice(0, 4)} ${clean.slice(4)}` : clean;
}
